import React from "react";
import styled from "styled-components";

// Button 컴포넌트의 props 타입을 정의합니다.
type ButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

// 기본 버튼 컴포넌트
const defaultButton: React.FC<ButtonProps> = (props) => (
  <button type="button" {...props}>
    {props.children}
  </button>
);

// Pagination 컴포넌트의 props 타입을 정의합니다.
interface PaginationProps {
  pages: number;
  page: number;
  PageButtonComponent?: React.ComponentType<ButtonProps>;
  onPageChange: (page: number) => void;
  previousText?: string;
  nextText?: string;
}

interface PaginationState {
  visiblePages: number[];
}

export default class Pagination extends React.Component<
  PaginationProps,
  PaginationState
> {
  constructor(props: PaginationProps) {
    super(props);
    this.changePage = this.changePage.bind(this);

    this.state = {
      visiblePages: this.getVisiblePages(null, props.pages),
    };
  }

  // componentDidUpdate(nextProps: PaginationProps) {
  //   if (this.props.pages !== nextProps.pages) {
  //     this.setState({
  //       visiblePages: this.getVisiblePages(null, nextProps.pages),
  //     });
  //   }

  //   this.changePage(nextProps.page + 1);
  // }

  componentDidUpdate(prevProps: PaginationProps) {
    // pages 수가 변경된 경우 visiblePages 재계산
    if (prevProps.pages !== this.props.pages) {
      this.setState({
        visiblePages: this.getVisiblePages(
          this.props.page + 1,
          this.props.pages,
        ),
      });
    }

    // page 번호가 바뀌었을 때만 visiblePages 재계산 (루프 방지)
    if (prevProps.page !== this.props.page) {
      this.setState({
        visiblePages: this.getVisiblePages(
          this.props.page + 1,
          this.props.pages,
        ),
      });
    }
  }

  filterPages = (visiblePages: number[], totalPages: number): number[] => {
    return visiblePages.filter((page) => page <= totalPages);
  };

  getVisiblePages = (page: number | null, total: number): number[] => {
    if (total < 7) {
      return this.filterPages([1, 2, 3, 4, 5, 6], total);
    } else {
      if (page !== null && page % 5 >= 0 && page > 4 && page + 2 < total) {
        return [1, page - 1, page, page + 1, total];
      } else if (
        page !== null &&
        page % 5 >= 0 &&
        page > 4 &&
        page + 2 >= total
      ) {
        return [1, total - 3, total - 2, total - 1, total];
      } else {
        return [1, 2, 3, 4, 5, total];
      }
    }
  };

  changePage = (page: number) => {
    const activePage = this.props.page + 1;

    if (page === activePage) {
      return;
    }

    const visiblePages = this.getVisiblePages(page, this.props.pages);

    this.setState({
      visiblePages: this.filterPages(visiblePages, this.props.pages),
    });

    this.props.onPageChange(page - 1);
  };

  render() {
    // 페이지 수가 2페이지 이상일 때 화면에 출력
    if (this.props.pages <= 1) return null;

    const { PageButtonComponent = defaultButton } = this.props;
    const { visiblePages } = this.state;
    const activePage = this.props.page + 1;

    return (
      <PaginationBox className="Table__pagination">
        <div className="Table__prevPageWrapper">
          <PageButtonComponent
            type="button"
            className="Table__pageButton"
            onClick={() => this.changePage(1)}
            disabled={activePage === 1}
          >
            처음
          </PageButtonComponent>
        </div>
        <div className="Table__prevPageWrapper">
          <PageButtonComponent
            type="button"
            className="Table__pageButton"
            onClick={() => {
              if (activePage === 1) return;
              this.changePage(activePage - 1);
            }}
            disabled={activePage === 1}
          >
            {this.props.previousText ? this.props.previousText : "이전 페이지"}
          </PageButtonComponent>
        </div>
        <div className="Table__visiblePagesWrapper">
          {visiblePages.map((page, index, array) => {
            return (
              <PageButtonComponent
                type="button"
                key={page}
                className={
                  activePage === page
                    ? "Table__pageButton Table__pageButton--active"
                    : "Table__pageButton"
                }
                onClick={this.changePage.bind(null, page)}
              >
                <span>{array[index - 1] + 2 < page ? `...` : page}</span>
              </PageButtonComponent>
            );
          })}
        </div>
        <div className="Table__nextPageWrapper">
          <PageButtonComponent
            type="button"
            className="Table__pageButton"
            onClick={() => {
              if (activePage === this.props.pages) return;
              this.changePage(activePage + 1);
            }}
            disabled={activePage === this.props.pages}
          >
            {this.props.nextText ? this.props.nextText : "다음 페이지"}
          </PageButtonComponent>
        </div>
        <div className="Table__nextPageWrapper">
          <PageButtonComponent
            type="button"
            className="Table__pageButton"
            onClick={() => this.changePage(this.props.pages)}
            disabled={activePage === this.props.pages}
          >
            끝
          </PageButtonComponent>
        </div>
      </PaginationBox>
    );
  }
}

//style
const PaginationBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2.4rem auto 0.5rem;
  .Table__visiblePagesWrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
  }
  button {
    width: 2.6rem;
    height: 2.6rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background-color: transparent;
    color: white;
    border-radius: 0.4rem;
    svg {
      width: 100%;
    }
    span {
      font-size: 1.2rem;
    }
    :disabled {
      opacity: 0.3;
    }
  }
  .Table__pageButton {
    background-color: white;
    color: #b0b8c1;
    &.Table__pageButton--active {
      background-color: #249fbf;
      color: white;
    }
  }
  .Table__prevPageWrapper,
  .Table__nextPageWrapper {
    > .Table__pageButton {
      background-color: transparent;
      width: auto;
      padding: 0 0.6rem;
    }
  }
`;
